import { FC, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import config from 'config';
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { getQuizGroupWithQuizzes } from 'store/quizGroup/quizGroupAsync';
import { selectQuizGroupData, selectQuizGroupLoading } from 'store/quizGroup/quizGroupSelectors';
// Components
import AppLoader from 'components/AppLoader';
import { Button } from 'components/Buttons';
import QuizGroupHeader from './QuizGroupHeader';
import QuizCard from './QuizCard';
import { findActiveQuizSlug } from './utils';

const QuizGroupQuizzesPage: FC = () => {
  const { groupSlug } = useParams<{ groupSlug: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const data = useAppSelector(selectQuizGroupData);
  const loading = useAppSelector(selectQuizGroupLoading);

  const activeCardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (groupSlug) dispatch(getQuizGroupWithQuizzes(groupSlug));
    // eslint-disable-next-line
  }, [groupSlug]);

  useEffect(() => {
    if (!data?.quizGroup) return;
    const { primaryColor, primaryFontColor, primaryButtonGradientStart, primaryButtonGradientEnd, primaryButtonFontColor } = data.quizGroup.theme || {};
    if (primaryColor) document.documentElement.style.setProperty('--primaryColor', primaryColor);
    if (primaryFontColor) document.documentElement.style.setProperty('--primaryFontColor', primaryFontColor);
    if (primaryButtonGradientStart) document.documentElement.style.setProperty('--primaryButtonGradientStart', primaryButtonGradientStart);
    if (primaryButtonGradientEnd) document.documentElement.style.setProperty('--primaryButtonGradientEnd', primaryButtonGradientEnd);
    if (primaryButtonFontColor) document.documentElement.style.setProperty('--primaryButtonFontColor', primaryButtonFontColor);
    // eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (activeCardRef.current) {
      activeCardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [data]);

  if (loading) return <AppLoader />;
  if (!data) return null;

  const { quizGroup, quizzes } = data;
  const activeQuizSlug = findActiveQuizSlug(quizzes);

  return (
    <div className="fixed inset-0 flex justify-center" style={{ background: '#0c141a' }}>
      <div
        className="relative w-full max-w-lg flex flex-col h-full"
        style={{
          background: quizGroup.backgroundImage
            ? `url('${quizGroup.backgroundImage}') center / cover`
            : 'linear-gradient(160deg, #0c141a 0%, #1d262d 100%)',
        }}
      >
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.45)', pointerEvents: 'none' }} />

        {/* fixed header */}
        <div className="relative z-10 shrink-0">
          <QuizGroupHeader
            name={quizGroup.name}
            logoImage={quizGroup.logoImage}
            primaryColor={quizGroup.theme?.primaryColor}
          />

          {quizGroup.publicLeaderboard?.enabled && (
            <div className="px-4 pb-3 flex justify-center">
              <Button
                className="primary-button"
                onClick={() => window.open(`${config.quizURL}/quiz-group/${quizGroup.slug}/live-leaderboard`, '_blank')}
              >
                {t('labels.leaderboard')}
              </Button>
            </div>
          )}

          <p className="text-white/60 text-xs font-medium uppercase tracking-widest text-center pb-2">
            {t('labels.quizzes')}
          </p>
        </div>

        {/* scrollable list */}
        <div className="relative flex-1 overflow-y-auto">
          <div className="px-4 pt-1 pb-6 flex flex-col gap-3">
            {quizzes.map((quiz: any, index: number) => {
              const isActive = quiz.quizNameSlug === activeQuizSlug;
              return (
                <div key={quiz.quizNameSlug || index} ref={isActive ? activeCardRef : null}>
                  <QuizCard
                    quiz={quiz}
                    theme={quizGroup.theme}
                    isActive={isActive}
                    onPlay={() => window.open(`/quiz/${quiz.quizNameSlug}`, '_blank')}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizGroupQuizzesPage;
